import React, { MutableRefObject, RefObject } from "react"
import { StarFilled } from "@ant-design/icons"
import { TourStep } from "../TourDocument"
import * as QuickAccessIcons from "../../../../libs/corporate/icons/outlined/quickAccessBar/QuickAccessBarCollection"
import { UpIcon } from "../../../../libs/corporate/icons/outlined/directions/ArrowsInCircleCollection"

export const MAIN_INTERFACE_TOUR_PLUGIN = "main_interface"

export interface MainInterfaceTourRefs {
    quickAccessMain: RefObject<HTMLDivElement>
    pharmacySelector: RefObject<HTMLDivElement>
    quickAccessAdditional: RefObject<HTMLDivElement>
    mainNavigation: RefObject<HTMLDivElement>
    firstMainMenuButton: RefObject<HTMLDivElement>
    firstMainSubMenu: RefObject<HTMLDivElement>
    favorites: RefObject<HTMLDivElement>
    search: RefObject<HTMLDivElement>
    notifications: RefObject<HTMLDivElement>
    bottomNavigation: RefObject<HTMLDivElement>
    newsButton: RefObject<HTMLDivElement>
    newsSettingsButton: RefObject<HTMLDivElement>
    newsSettingsPanel: RefObject<HTMLDivElement>
    logoutButton: RefObject<HTMLDivElement>
}

export interface MainInterfaceTourActions {
    firstMainMenuButton: RefObject<HTMLDivElement>
    showNewsButton: MutableRefObject<(() => void) | null>
    openNewsModal: MutableRefObject<(() => void) | null>
    openNewsSettings: MutableRefObject<(() => void) | null>
    closeNewsModal: MutableRefObject<(() => void) | null>
    closeNewsSettings: MutableRefObject<(() => void) | null>
}

export interface MainInterfaceTourOptions {
    isAdministrator: boolean
}

const iconRowStyle = { display: "flex", alignItems: "center", margin: "5px", gap: "10px" }
const smallIconWrapperStyle = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: "22px", minWidth: "22px", height: "22px" }

const waitFor = (condition: () => boolean, timeout = 2000) => {
    return new Promise<boolean>((resolve) => {
        const start = Date.now()

        const check = () => {
            if (condition()) {
                resolve(true)
                return
            }

            if (Date.now() - start >= timeout) {
                resolve(false)
                return
            }

            window.requestAnimationFrame(check)
        }

        check()
    })
}

const isVisible = (element: HTMLElement | null) => {
    return Boolean(element && element.isConnected && element.getClientRects().length > 0 && window.getComputedStyle(element).display !== "none")
}

const clickFirstMainMenuButton = (actions: MainInterfaceTourActions) => {
    actions.firstMainMenuButton.current?.click()
}

const ensureFirstMainSubMenuOpened = async (refs: MainInterfaceTourRefs, actions: MainInterfaceTourActions) => {
    if (!isVisible(refs.firstMainSubMenu.current)) {
        clickFirstMainMenuButton(actions)
    }

    await waitFor(() => isVisible(refs.firstMainSubMenu.current))
}

const ensureFirstMainSubMenuClosed = async (refs: MainInterfaceTourRefs, actions: MainInterfaceTourActions) => {
    if (isVisible(refs.firstMainSubMenu.current)) {
        clickFirstMainMenuButton(actions)
    }

    await waitFor(() => !isVisible(refs.firstMainSubMenu.current))
}

const showNewsButton = async (refs: MainInterfaceTourRefs, actions: MainInterfaceTourActions) => {
    actions.showNewsButton.current?.()

    await waitFor(() => isVisible(refs.newsButton.current))
}

const openNewsModal = async (refs: MainInterfaceTourRefs, actions: MainInterfaceTourActions) => {
    actions.openNewsModal.current?.()

    await waitFor(() => isVisible(refs.newsSettingsButton.current))
}

const openNewsSettings = async (refs: MainInterfaceTourRefs, actions: MainInterfaceTourActions) => {
    actions.openNewsSettings.current?.()

    await waitFor(() => isVisible(refs.newsSettingsPanel.current))
}

const closeNewsModal = async (actions: MainInterfaceTourActions) => {
    actions.closeNewsSettings.current?.()
    actions.closeNewsModal.current?.()
}

export const getMainInterfaceTourSteps = (
    refs: MainInterfaceTourRefs,
    actions: MainInterfaceTourActions,
    options: MainInterfaceTourOptions,
): TourStep[] => [
    {
        target: () => refs.quickAccessMain.current,
        description: (
            <div>
                Верхняя панель содержит быстрый доступ к основным функциям системы:
                <div style={iconRowStyle}><QuickAccessIcons.MarketingEventsIcon /> Маркетинговые мероприятия</div>
                <div style={iconRowStyle}><QuickAccessIcons.MarketingCompensatedDiscountsIcon /> Компенсируемые скидки</div>
                <div style={iconRowStyle}><QuickAccessIcons.FileImportIcon /> Импорт накладных</div>
                <div style={iconRowStyle}><QuickAccessIcons.ArrivalDocumentsIcon /> Приходные накладные</div>
                <div style={iconRowStyle}><QuickAccessIcons.ExpenseDocumentsIcon /> Расходные накладные</div>
                <div style={iconRowStyle}><QuickAccessIcons.TransferProductIcon /> Перемещение товара</div>
                <div style={iconRowStyle}><QuickAccessIcons.ReturnSupplierIcon /> Акты возврата поставщику</div>
                <div style={iconRowStyle}><QuickAccessIcons.ActRevaluationIcon /> Акты переоценки</div>
                <div style={iconRowStyle}><QuickAccessIcons.ActDismantlingIcon /> Акты разукомплектации</div>
                <div style={iconRowStyle}><QuickAccessIcons.ActDisagregationIcon /> Акты разагрегации</div>
                <div style={iconRowStyle}><QuickAccessIcons.StockBalancesIcon /> Товарные остатки</div>
                <div style={iconRowStyle}><QuickAccessIcons.DefectIcon /> Дефектура</div>
                <div style={iconRowStyle}><QuickAccessIcons.CashShiftsIcon /> Кассовые смены</div>
                <div style={iconRowStyle}><QuickAccessIcons.CashiersCheckIcon /> Кассовые чеки</div>
                <div style={iconRowStyle}><QuickAccessIcons.DiscountsIcon /> Скидки</div>
                <div style={iconRowStyle}><QuickAccessIcons.CalculatorIcon /> Калькулятор</div>
                <div style={iconRowStyle}><QuickAccessIcons.EsDataExchangeIcon /> ЕС</div>
                <div style={iconRowStyle}><QuickAccessIcons.DashboardBusinessControlsIcon /> Конструктор бизнес-контролей</div>
            </div>
        ),
        placement: "bottom",
    },
    {
        target: () => refs.pharmacySelector.current,
        description: "Список доступных аптек. Выбор аптек влияет на отображение документов в журналах - будут отображены документы только выделенных аптек. Выбор основной аптеки влияет на автозаполнение строки «Аптека» в документах.",
        placement: "bottom",
    },
    {
        target: () => refs.quickAccessAdditional.current,
        description: (
            <div>
                Дополнительные кнопки быстрого доступа:
                <div style={iconRowStyle}><QuickAccessIcons.AlertsHistoryJournalIcon /> Журнал сообщений - здесь отображаются все системные сообщения и ошибки</div>
                <div style={iconRowStyle}>
                    <span className="f3-tour-step-small-icon" style={smallIconWrapperStyle}>
                        <QuickAccessIcons.TourEducationIcon />
                    </span>
                    Пройти обучение - запуск интерактивного обучения по активной вкладке. <br/> "Пиктограмма Х" - прерывание обучение. При последующем запуске обучения оно продолжится с места, на котором прервались. <br/> "Завершить" - завершение обучения. При последующем запуске обучение начнётся заново
                </div>
                {options.isAdministrator && (
                    <div style={iconRowStyle}><QuickAccessIcons.ReferenceIconAdmin /> Открыть руководство администратора - открытие справки администратора</div>
                )}
                <div style={iconRowStyle}><QuickAccessIcons.ReferenceIcon /> Открыть справку - открытие справки пользователя</div>
                <div style={iconRowStyle}><UpIcon /> Свернуть панель - сворачивание панели быстрого доступа</div>
            </div>
        ),
        placement: "bottom",
    },
    {
        target: () => refs.mainNavigation.current,
        description: (
            <div>
                Боковое меню обеспечивает навигацию по основным разделам системы:
                <ul>
                    <li>Документы - работа с накладными, актами и документами</li>
                    <li>Справочники - номенклатура, контрагенты, склады и другие справочники</li>
                    <li>Учет - товарные остатки, кассовые операции, инвентаризация и другие журналы</li>
                    <li>Отчёты - аналитика и отчетность</li>
                    <li>Файл - импорт/экспорт данных</li>
                </ul>
            </div>
        ),
        placement: "right",
        onNext: async () => {
            await ensureFirstMainSubMenuOpened(refs, actions)
        },
    },
    {
        target: () => refs.firstMainSubMenu.current,
        keepIfHidden: true,
        description: (
            <div>
                Чтобы добавить часто используемый функционал в быстрый доступ, необходимо нажать на звездочку {" "}<StarFilled style={{ color: "rgb(52, 73, 94)", fontSize: 15 }}/>{" "} рядом с названием. Элемент появится в разделе «Избранное». Чтобы убрать элемент из быстрого доступа, снова нажмите на желтую звездочку рядом с его названием - и он исчезнет из списка.
            </div>
        ),
        placement: "right",
        onEnter: async () => {
            await ensureFirstMainSubMenuOpened(refs, actions)
        },
        onNext: async () => {
            await ensureFirstMainSubMenuClosed(refs, actions)
        },
    },
    {
        target: () => refs.favorites.current,
        description: "Раздел «Избранное» содержит все указанные избранными элементы, чтобы быстро открывать нужный функционал без поиска по основным категориям. Чтобы убрать элемент из быстрого доступа нажмите на крестик рядом с его названием.",
        placement: "right",
        onEnter: async () => {
            await ensureFirstMainSubMenuClosed(refs, actions)
        },
    },
    {
        target: () => refs.search.current,
        description: "Поиск позволяет производить поиск по документам, справочникам и прочему функционалу.",
        placement: "right",
    },
    {
        target: () => refs.notifications.current,
        description: "В области уведомлений отображаются системные сообщения и уведомления о выполненных операциях. Для детального просмотра нажмите «Открыть». Для закрытия уведомления нажмите на крестик в правом верхнем углу уведомления.",
        placement: "right",
    },
    {
        target: () => refs.bottomNavigation.current,
        description: (
            <ul>
                <li>Сервис - системные справочники, настройки и информация</li>
                <li>Администрирование - управление пользователями, ролями и интерфейсом</li>
                <li>Имя и роль пользователя, активного в данный момент</li>
            </ul>
        ),
        placement: "right",
    },
    {
        target: () => refs.newsButton.current,
        description: "Кнопка «Новости» предназначена для просмотра системных объявлений, анонсов обновлений и важных сообщений от службы поддержки. Новые сообщения маркируются индикатором.",
        placement: "left",
        onEnter: async () => {
            await showNewsButton(refs, actions)
        },
        onNext: async () => {
            await openNewsModal(refs, actions)
        },
        onClose: () => {
            closeNewsModal(actions)
        },
    },
    {
        target: () => refs.newsSettingsButton.current,
        keepIfHidden: true,
        description: "Кнопка настройки уведомлений",
        placement: "left",
        onEnter: async () => {
            await openNewsModal(refs, actions)
        },
        onNext: async () => {
            await openNewsSettings(refs, actions)
        },
        onClose: () => {
            closeNewsModal(actions)
        },
    },
    {
        target: () => refs.newsSettingsPanel.current,
        keepIfHidden: true,
        description: "Здесь можно указать положение кнопки и окна новостей, время хранение новостей, включить/выключить звуковое уведомление, настроить прозрачность, либо сбросить настройки по умолчанию.",
        placement: "left",
        onEnter: async () => {
            await openNewsModal(refs, actions)
            await openNewsSettings(refs, actions)
        },
        onNext: async () => {
            await closeNewsModal(actions)
        },
        onClose: () => {
            closeNewsModal(actions)
        },
    },
    {
        target: () => refs.logoutButton.current,
        description: "Кнопка выхода на страницу авторизации.",
        placement: "right",
        onEnter: async () => {
            await closeNewsModal(actions)
        },
    },
]
